import { useNavigate } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import type { LucideIcon } from 'lucide-react';
import {
  Calculator,
  Eye,
  FileText,
  Info,
  Lock,
  Pencil,
  Scale,
  Shield,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdvisor } from '../../context/useAdvisorContext';
import { getArrayValue } from '../../helpers/safeRecordAccess';
import { useInsuranceType } from '../../hooks/useInsuranceType';

export interface AccordionItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface AccordionData {
  icon: LucideIcon;
  title: string;
  items: AccordionItem[];
}

function buildConsentData(
  t: TFunction,
  advisorBadgeText: string,
  accordion1Items: AccordionItem[],
  accordion2Items: AccordionItem[]
) {
  return {
    advisorBadge: advisorBadgeText,
    title: t('consent.title'),
    subtitle: t('consent.subtitle'),
    accordion1: {
      icon: Info,
      title: t('consent.accordion1.title'),
      items: accordion1Items,
    } as AccordionData,
    accordion2: {
      icon: ShieldCheck,
      title: t('consent.accordion2.title'),
      items: accordion2Items,
    } as AccordionData,
    contact: {
      label: t('consent.contact.label'),
      name: t('consent.contact.name'),
      email: t('consent.contact.email'),
      phone: t('consent.contact.phone'),
    },
    warning: {
      title: t('consent.warning.title'),
      description: t('consent.warning.description'),
    },
    buttons: {
      accept: t('consent.buttons.accept'),
      refuse: t('consent.buttons.refuse'),
      disabledHint: t('consent.buttons.disabledHint'),
    },
  };
}

function buildAccordionItems(
  rawItems: Array<{ title: string; description: string }>,
  icons: LucideIcon[]
): AccordionItem[] {
  return rawItems.map((item, index) => ({
    ...item,
    icon: getArrayValue(icons, index),
  }));
}

export const useConsent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedType, clearSelection } = useInsuranceType();
  const { hasAdvisor, advisorDisplayName } = useAdvisor();

  const [accordion1Open, setAccordion1Open] = useState(false);
  const [accordion2Open, setAccordion2Open] = useState(false);
  const [accordion1HasOpened, setAccordion1HasOpened] = useState(false);
  const [accordion2HasOpened, setAccordion2HasOpened] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);

  const canAccept = accordion1HasOpened && accordion2HasOpened;

  useEffect(() => {
    if (!selectedType) {
      navigate({ to: '/form' });
    }
  }, [selectedType, navigate]);

  const accordion1Icons: LucideIcon[] = [FileText, Calculator, Shield, Scale];
  const accordion2Icons: LucideIcon[] = [Eye, Pencil, Trash2, Lock];

  const accordion1Items = buildAccordionItems(
    t('consent.accordion1.items', { returnObjects: true }) as Array<{
      title: string;
      description: string;
    }>,
    accordion1Icons
  );

  const accordion2Items = buildAccordionItems(
    t('consent.accordion2.items', { returnObjects: true }) as Array<{
      title: string;
      description: string;
    }>,
    accordion2Icons
  );

  const advisorBadgeText = hasAdvisor
    ? `${t('advisor.badgePrefix')}: ${advisorDisplayName}`
    : t('consent.advisorBadge');

  const data = buildConsentData(
    t,
    advisorBadgeText,
    accordion1Items,
    accordion2Items
  );

  const handleAccept = () => {
    if (canAccept && selectedType) {
      setShowNameModal(true);
    }
  };

  const handleNameSubmit = (firstName: string, lastName: string) => {
    if (selectedType) {
      return navigate({
        to: '/chat/$type',
        params: { type: selectedType },
        state: { userInfo: { firstName, lastName } } as Record<string, unknown>,
      });
    }
  };

  const handleCloseNameModal = () => {
    setShowNameModal(false);
  };

  const handleRefuse = () => {
    clearSelection();
    navigate({ to: '/' });
  };

  const toggleAccordion1 = () => {
    const newState = !accordion1Open;
    setAccordion1Open(newState);
    if (newState) {
      setAccordion1HasOpened(true);
    }
  };

  const toggleAccordion2 = () => {
    const newState = !accordion2Open;
    setAccordion2Open(newState);
    if (newState) {
      setAccordion2HasOpened(true);
    }
  };

  return {
    data,
    accordion1Open,
    accordion2Open,
    toggleAccordion1,
    toggleAccordion2,
    handleAccept,
    handleRefuse,
    canAccept,
    showNameModal,
    handleNameSubmit,
    handleCloseNameModal,
  };
};

export type ConsentProps = ReturnType<typeof useConsent>;
